import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Phone,
  ChevronDown,
  Shield,
  FileText,
  Search,
  ShoppingBag,
  Building2,
  MessageCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { usePopup } from "../context/PopupContext";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { openPopup } = usePopup();

  const services = [
    {
      name: "Trademark Registration",
      href: "/trademark-registration",
      icon: Shield,
      description: "Secure your brand identity",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      name: "Copyright Registration",
      href: "/copyright-registration",
      icon: FileText,
      description: "Protect your creative work",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      name: "Trademark Search",
      href: "/trademark-search",
      icon: Search,
      description: "Check availability instantly",
      color: "text-cyan-600",
      bgColor: "bg-cyan-50",
    },
    {
      name: "Amazon Brand Registry",
      href: "/amazon-brand-registry",
      icon: ShoppingBag,
      description: "Protect your Amazon listings",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      name: "Company Formation",
      href: "/company-formation",
      icon: Building2,
      description: "Start your business right",
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
  ];

  const navigation = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about-us" },
    { name: "Contact Us", href: "/contact-us" },
  ];

  const isActive = (path) => location.pathname === path;
  const isServiceActive = () =>
    services.some((service) => service.href === location.pathname);

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/assets/img/logo.png"
              alt="Trademark Claim Experts"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-1">
            {navigation.slice(0, 1).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                  isActive(item.href)
                    ? "text-brand-purple bg-gradient-to-r from-brand-purple/5 to-brand-pink/10"
                    : "text-gray-700 hover:text-brand-purple hover:bg-gray-50"
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* Services Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className={`px-4 py-2 text-sm font-semibold rounded-md transition-all flex items-center gap-1 ${
                  isServiceActive()
                    ? "text-brand-purple bg-gradient-to-r from-brand-purple/5 to-brand-pink/10"
                    : "text-gray-700 hover:text-brand-purple hover:bg-gray-50"
                }`}
              >
                Services
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Dropdown Menu */}
              <div
                className={`absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 ${
                  servicesOpen
                    ? "opacity-100 visible translate-y-0"
                    : "opacity-0 invisible -translate-y-2"
                }`}
              >
                <div className="p-2">
                  {services.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <Link
                        key={service.name}
                        to={service.href}
                        className={`flex items-start gap-3 p-3 rounded-lg transition-all duration-200 hover:bg-gray-50 group/item ${
                          isActive(service.href)
                            ? "bg-gradient-to-r from-brand-purple/5 to-brand-pink/10"
                            : ""
                        }`}
                        style={{
                          animationDelay: `${index * 50}ms`,
                          animation: servicesOpen
                            ? "slideIn 0.3s ease-out forwards"
                            : "none",
                        }}
                      >
                        <div
                          className={`w-10 h-10 ${service.bgColor} rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover/item:scale-110 group-hover/item:shadow-md`}
                        >
                          <Icon
                            className={`w-5 h-5 ${service.color} transition-all duration-300 group-hover/item:scale-110`}
                          />
                        </div>
                        <div className="flex-1">
                          <h3
                            className={`font-semibold text-sm mb-0.5 transition-colors ${
                              isActive(service.href)
                                ? "text-brand-purple"
                                : "text-gray-900 group-hover/item:text-brand-purple"
                            }`}
                          >
                            {service.name}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {service.description}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {navigation.slice(1).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                  isActive(item.href)
                    ? "text-brand-purple bg-gradient-to-r from-brand-purple/5 to-brand-pink/10"
                    : "text-gray-700 hover:text-brand-purple hover:bg-gray-50"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex lg:items-center lg:space-x-4">
            <a
              href="tel:+18778866927"
              className="flex items-center text-sm font-medium text-gray-700 hover:text-brand-purple transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" />
              1-877-886-6927
            </a>
            <button className="js-chat flex items-center text-sm font-medium text-gray-700 hover:text-brand-purple transition-colors">
              <MessageCircle className="w-4 h-4 mr-2" />
              Live Chat
            </button>
            <Button
              onClick={openPopup}
              className="bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white shadow-lg hover:shadow-xl transition-all"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <div className="space-y-1">
              <Link
                to="/"
                className={`block px-3 py-2 text-base font-medium rounded-md ${
                  isActive("/")
                    ? "text-white bg-gradient-to-r from-brand-purple to-brand-pink"
                    : "text-gray-700 hover:text-brand-purple hover:bg-gray-50"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>

              {/* Mobile Services */}
              <div className="px-3 py-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Services
                </p>
                <div className="space-y-1 ml-2">
                  {services.map((service) => {
                    const Icon = service.icon;
                    return (
                      <Link
                        key={service.name}
                        to={service.href}
                        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md ${
                          isActive(service.href)
                            ? "text-white bg-gradient-to-r from-brand-purple to-brand-pink"
                            : "text-gray-700 hover:text-brand-purple hover:bg-gray-50"
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Icon className={`w-4 h-4 ${service.color}`} />
                        {service.name}
                      </Link>
                    );
                  })}
                </div>
              </div>

              <Link
                to="/about-us"
                className={`block px-3 py-2 text-base font-medium rounded-md ${
                  isActive("/about-us")
                    ? "text-white bg-gradient-to-r from-brand-purple to-brand-pink"
                    : "text-gray-700 hover:text-brand-purple hover:bg-gray-50"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                About Us
              </Link>

              <Link
                to="/contact-us"
                className={`block px-3 py-2 text-base font-medium rounded-md ${
                  isActive("/contact-us")
                    ? "text-white bg-gradient-to-r from-brand-purple to-brand-pink"
                    : "text-gray-700 hover:text-brand-purple hover:bg-gray-50"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Us
              </Link>
            </div>
            <div className="mt-4 pt-4 border-t space-y-3">
              <a
                href="tel:+18778866927"
                className="flex items-center px-3 py-2 text-base font-medium text-gray-700"
              >
                <Phone className="w-5 h-5 mr-2" />
                1-877-886-6927
              </a>
              <button className="js-chat flex items-center px-3 py-2 text-base font-medium text-gray-700">
                <MessageCircle className="w-5 h-5 mr-2" />
                Live Chat
              </button>
              <Button
                onClick={() => {
                  setMobileMenuOpen(false);
                  openPopup();
                }}
                className="w-full bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
